import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import OpsPlanSectionTabs, { OPS_PLAN_TAB_MANAGEMENT } from "../components/OpsPlanSectionTabs";
import SectionTabsPageHeader from "../components/SectionTabsPageHeader";
import { validateOptionalDate } from "../shared/utils/validation";
import { fiscalYearsMatch, resolveMatchingFiscalYear, canonicalizeFiscalYear } from "../shared/utils/fiscalYearMatch";
import { parseOptionalFiniteNumber } from "../shared/utils/csvNumber";
import ConfirmationModal from "../components/ConfirmationModal";
import { TableActionColumnHeader } from "../components/TableShell";
import SopEditorModal from "../components/SopEditorModal";
import OperatingPlanFyStatsRow from "../components/OperatingPlanFyStatsRow";
import { IconEdit, IconDelete, iconBtnStyle, RowActionIcons } from "../shared/icons/IconLib";
import ImportCsvPanel from "../components/entity/ImportCsvPanel";
import { parseCsvTextToNormalizedTable, findCsvColumnIndex } from "../shared/utils/csvParse";
import { attachValidatedCsvFileImport, downloadCsvTextFile } from "../shared/utils/csvFileHelpers";

import { apiFetch } from "../shared/api/apiFetch";
import {
  formatSelectedCountSuffix,
  intersectSelectedIdsWithRows,
} from "../shared/utils/entityListSelection";
import {
  OPS_PLAN_ACTUAL_EVENTS_TITLE,
  OPS_PLAN_CSV_COLUMN_HINT,
  OPS_PLAN_LABEL_ACTUAL_ENTERED_HRS,
  OPS_PLAN_LABEL_ACTUAL_TOTAL_HRS,
  OPS_PLAN_SOP_COL_ENTERED_HRS,
  OPS_PLAN_SOP_COL_EVENTS_HRS,
  OPS_PLAN_SOP_COL_TOTAL_HRS,
  OPS_PLAN_SOP_COL_REVENUE,
  OPS_PLAN_SOP_COL_EXPENSES,
  OPS_PLAN_SOP_COL_COST_PER_KID_HOUR,
  OPS_PLAN_SOP_COST_PER_KID_HOUR_TITLE,
  OPS_PLAN_LABEL_PLAN_HRS,
  OPS_PLAN_LABEL_YTD_ACTUAL_HRS,
  OPS_PLAN_LABEL_YTD_PLAN_HRS,
  OPS_PLAN_SOP_EMPTY_MESSAGE,
  formatOpsPlanHrs,
  formatSopPlanHrsByProgramHint,
  roundOpsPlanHrsUp,
  sopEventActualMapKey,
} from "../shared/utils/opsPlanLabels";
import { formatCurrencyUsd } from "../shared/utils/financeDisplay";
import { computeCostPerKidHour, formatCostPerKidHour, parseSopMoneyField } from "../shared/utils/sopFinance";
import { buildSopEventActualDrillPath } from "../shared/utils/aopActualEventsDrill";

const dayjs = require("dayjs");
const uiConfig = require("../uiConfig");
const { getMessageForFetchError } = require("../shared/utils/apiError");
const ErrorBanner = require("../components/ErrorBanner").default;

const TAB_SOP = "sop";
const TAB_AOP_BY_PROGRAM = "aopByProgram";
const TAB_SOP_BY_YEAR_PROGRAM = "sopByYearProgram";

const sopActualDrillLinkStyle = {
  color: "#0d6efd",
  textDecoration: "none",
};

// Extract numeric year for sorting (e.g. "FY2026" or "2026" -> 2026)
function fySortKey(fy) {
  if (!fy || typeof fy !== "string") return 0;
  const m = fy.trim().match(/\d{4}/);
  return m ? parseInt(m[0], 10) : 0;
}

const CHART_MAX_WIDTH = 900;
const CHART_MAX_HEIGHT = 360;
function SopByYearBarChart({ data, seriesKeys, colors }) {
  const containerRef = useRef(null);
  const [size, setSize] = React.useState({ width: CHART_MAX_WIDTH, height: CHART_MAX_HEIGHT });
  const [hover, setHover] = React.useState(null);
  React.useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect;
      setSize({
        width: Math.min(CHART_MAX_WIDTH, Math.max(200, width)),
        height: Math.min(CHART_MAX_HEIGHT, Math.max(180, height)),
      });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  if (!data.length || !seriesKeys.length) return null;
  const padding = { top: 24, right: 24, bottom: 36, left: 52 };
  const chartW = size.width - padding.left - padding.right;
  const chartH = size.height - padding.top - padding.bottom;
  const allValues = data.flatMap((d) => seriesKeys.map((k) => d[k] || 0));
  const yMax = Math.max(1, Math.ceil(Math.max(...allValues) * 1.05));
  const yScale = (v) => padding.top + chartH - (v / yMax) * chartH;
  const nYears = data.length;
  const nSeries = seriesKeys.length;
  const yearGap = 16; // space between each fiscal year
  const totalYearGaps = Math.max(0, nYears - 1) * yearGap;
  const groupWidth = (chartW - totalYearGaps) / Math.max(1, nYears);
  const barGap = 2;
  const barWidth = Math.max(2, (groupWidth - barGap * (nSeries - 1)) / nSeries);
  const xCenter = (i) => padding.left + i * (groupWidth + yearGap) + groupWidth / 2;
  const yTicks = [];
  for (let i = 0; i <= 6; i++) {
    const v = (yMax * i) / 6;
    yTicks.push({ v, y: yScale(v) });
  }
  const xTickStep = Math.max(1, Math.floor(nYears / 10));
  const xTicks = data.filter((_, i) => i % xTickStep === 0 || i === nYears - 1).map((d) => d.year);
  return (
    <div
      ref={containerRef}
      style={{
        position: "relative",
        width: "100%",
        maxWidth: CHART_MAX_WIDTH,
        height: CHART_MAX_HEIGHT + 44,
        minHeight: 220,
        boxSizing: "border-box",
      }}
    >
      <div
        style={{
          border: "1px solid #e9ecef",
          borderRadius: "8px",
          padding: "12px",
          backgroundColor: "#fafbfc",
          boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
        }}
      >
        <svg width={size.width} height={size.height} style={{ overflow: "visible", display: "block" }}>
          <defs>
            <pattern id="sop-by-year-grid" width={40} height={40} patternUnits="userSpaceOnUse">
              <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#e9ecef" strokeWidth="0.5" />
            </pattern>
          </defs>
          <rect x={padding.left} y={padding.top} width={chartW} height={chartH} fill="url(#sop-by-year-grid)" />
          <text x={padding.left + chartW / 2} y={size.height - 6} textAnchor="middle" fontSize={11} fill="#495057">
            Fiscal Year
          </text>
          <text
            x={14}
            y={padding.top + chartH / 2}
            textAnchor="middle"
            fontSize={11}
            fill="#495057"
            transform={`rotate(-90, 14, ${padding.top + chartH / 2})`}
          >
            {OPS_PLAN_LABEL_PLAN_HRS}
          </text>
          {yTicks.map(({ v, y }) => (
            <g key={v}>
              <line x1={padding.left} y1={y} x2={padding.left + chartW} y2={y} stroke="#dee2e6" strokeWidth={1} />
              <text x={padding.left - 6} y={y + 4} textAnchor="end" fontSize={10} fill="#6c757d">
                {v === 0 ? "0" : Number(v).toLocaleString()}
              </text>
            </g>
          ))}
          {xTicks.map((year) => {
            const i = data.findIndex((d) => d.year === year);
            if (i < 0) return null;
            const x = xCenter(i);
            return (
              <g key={year}>
                <text x={x} y={padding.top + chartH + 18} textAnchor="middle" fontSize={10} fill="#6c757d">
                  {year}
                </text>
              </g>
            );
          })}
          {seriesKeys.map((key, seriesIdx) =>
            data.map((d, j) => {
              const v = d[key] != null ? d[key] : 0;
              if (v <= 0) return null;
              const baseX = padding.left + j * (groupWidth + yearGap);
              const offset = seriesIdx * (barWidth + barGap);
              const x = baseX + offset;
              const barH = (v / yMax) * chartH;
              const y = yScale(v);
              const isHover = hover && hover.year === d.year && hover.key === key;
              return (
                <g key={`${key}-${j}`}>
                  <rect
                    x={x}
                    y={y}
                    width={barWidth}
                    height={barH}
                    fill={colors[seriesIdx % colors.length]}
                    stroke={isHover ? "#1e3a8a" : "#fff"}
                    strokeWidth={isHover ? 2 : 1}
                    rx={2}
                    onMouseEnter={() => setHover({ year: d.year, key, value: v })}
                    onMouseLeave={() => setHover(null)}
                    style={{ cursor: "pointer" }}
                  />
                  <text
                    x={x + barWidth / 2}
                    y={y - 4}
                    textAnchor="middle"
                    fontSize={9}
                    fill="#374151"
                    fontWeight={500}
                    style={{ pointerEvents: "none" }}
                  >
                    {Number(v).toLocaleString()}
                  </text>
                </g>
              );
            })
          )}
        </svg>
        {hover && (
          <div
            style={{
              position: "absolute",
              left: padding.left + 20,
              top: padding.top + 16,
              background: "rgba(255,255,255,0.98)",
              padding: "6px 10px",
              borderRadius: 6,
              boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
              fontSize: 12,
              pointerEvents: "none",
            }}
          >
            <strong>{hover.year}</strong> · {hover.key}: {Number(hover.value).toLocaleString()} hrs
          </div>
        )}
        <div style={{ display: "flex", flexWrap: "wrap", gap: "12px 20px", marginTop: 8, fontSize: 12 }}>
          {seriesKeys.map((key, i) => (
            <span key={key} style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span
                style={{
                  width: 12,
                  height: 12,
                  backgroundColor: colors[i % colors.length],
                  borderRadius: 2,
                }}
              />
              {key}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

// Vertical grouped bar chart: YTD Plan vs YTD Actual by program + Total at end (3D style)
const AOP_PROGRESS_CHART_COLORS = { ytdPlan: "#f4c430", ytdActual: "#212529" }; // yellow, black
const AOP_CHART_3D_DEPTH = 5; // px depth for 3D effect
const AOP_CHART_HEIGHT = 320;
const AOP_CHART_PADDING = { top: 44, right: 24 + AOP_CHART_3D_DEPTH, bottom: 56, left: 48 };

function darkenHex(hex, pct) {
  const n = parseInt(hex.slice(1), 16);
  const r = Math.max(0, ((n >> 16) & 0xff) * (1 - pct));
  const g = Math.max(0, ((n >> 8) & 0xff) * (1 - pct));
  const b = Math.max(0, (n & 0xff) * (1 - pct));
  return `#${((1 << 24) + (r << 16) + (g << 8) + (b | 0)).toString(16).slice(1)}`;
}
function lightenHex(hex, pct) {
  const n = parseInt(hex.slice(1), 16);
  const r = (n >> 16) & 0xff;
  const g = (n >> 8) & 0xff;
  const b = n & 0xff;
  const r2 = Math.min(255, Math.floor(r + (255 - r) * pct));
  const g2 = Math.min(255, Math.floor(g + (255 - g) * pct));
  const b2 = Math.min(255, Math.floor(b + (255 - b) * pct));
  return `#${((1 << 24) + (r2 << 16) + (g2 << 8) + (b2 | 0)).toString(16).slice(1)}`;
}

// Prefer a tight axis max just above the data (minimal empty space at top).
function niceYMax(val) {
  if (val <= 0) return 1;
  let pad = val * 1.05; // ~5% headroom so top bar doesn't touch axis
  let magnitude = Math.pow(10, Math.floor(Math.log10(pad)));
  let normalized = pad / magnitude;
  while (normalized > 10) {
    magnitude *= 10;
    normalized /= 10;
  }
  const steps = [1, 1.2, 1.5, 2, 2.5, 5, 7, 10];
  const step = steps.find((s) => s >= normalized) || 10;
  return magnitude * step;
}

export function AopProgressBarChart({ rows, totalYtdPlan = 0, totalYtdActual = 0 }) {
  const containerRef = useRef(null);
  const [width, setWidth] = React.useState(700);
  React.useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      setWidth(Math.max(320, entries[0].contentRect.width));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  if (!rows || rows.length === 0) return null;
  const categories = [...rows.map((r) => r.program), "Total"];
  const data = [
    ...rows.map((r) => ({
      label: r.program,
      ytdPlan: roundOpsPlanHrsUp(r.ytdPlan),
      ytdActual: roundOpsPlanHrsUp(r.ytdActual),
    })),
    {
      label: "Total",
      ytdPlan: roundOpsPlanHrsUp(totalYtdPlan),
      ytdActual: roundOpsPlanHrsUp(totalYtdActual),
    },
  ];
  const maxVal = Math.max(
    1,
    ...data.flatMap((d) => [d.ytdPlan, d.ytdActual])
  );
  const yMax = niceYMax(maxVal);
  const chartW = width - AOP_CHART_PADDING.left - AOP_CHART_PADDING.right;
  const chartH = AOP_CHART_HEIGHT - AOP_CHART_PADDING.top - AOP_CHART_PADDING.bottom;
  const yScale = (v) =>
    AOP_CHART_PADDING.top + chartH - (v / yMax) * chartH;
  const n = categories.length;
  const groupGap = 12;
  const groupWidth = (chartW - groupGap * (n - 1)) / n;
  const barGap = 4;
  const barWidth = Math.max(4, (groupWidth - barGap) / 2);
  const groupCenter = (i) =>
    AOP_CHART_PADDING.left + i * (groupWidth + groupGap) + groupWidth / 2;
  const barLeft = (i, which) => {
    const left = AOP_CHART_PADDING.left + i * (groupWidth + groupGap);
    return which === "plan" ? left + barGap / 2 : left + barWidth + barGap;
  };
  const chartBottom = AOP_CHART_PADDING.top + chartH;
  const yTicks = [];
  for (let step = 0; step <= 5; step++) {
    const v = (yMax * step) / 5;
    yTicks.push({ v, y: yScale(v) });
  }
  const totalHeight = AOP_CHART_HEIGHT;
  return (
    <div ref={containerRef} style={{ width: "100%", maxWidth: 900, boxSizing: "border-box" }}>
      <div
        style={{
          border: "1px solid #e9ecef",
          borderRadius: "8px",
          padding: "16px",
          backgroundColor: "#fff",
          boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
        }}
      >
        {/* Legend at top */}
        <div style={{ display: "flex", justifyContent: "center", gap: "24px", marginBottom: 8, fontSize: 12 }}>
          <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span
              style={{
                width: 14,
                height: 14,
                backgroundColor: AOP_PROGRESS_CHART_COLORS.ytdPlan,
                borderRadius: 2,
              }}
            />
            {OPS_PLAN_LABEL_YTD_PLAN_HRS}
          </span>
          <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span
              style={{
                width: 14,
                height: 14,
                backgroundColor: AOP_PROGRESS_CHART_COLORS.ytdActual,
                borderRadius: 2,
              }}
            />
            {OPS_PLAN_LABEL_YTD_ACTUAL_HRS}
          </span>
        </div>
        <svg width={width} height={totalHeight} style={{ overflow: "visible", display: "block" }}>
          {/* Y-axis grid and labels */}
          {yTicks.map(({ v, y }) => (
            <g key={v}>
              <line
                x1={AOP_CHART_PADDING.left}
                y1={y}
                x2={AOP_CHART_PADDING.left + chartW}
                y2={y}
                stroke="#e9ecef"
                strokeWidth={1}
              />
              <text
                x={AOP_CHART_PADDING.left - 8}
                y={y + 4}
                textAnchor="end"
                fontSize={10}
                fill="#6c757d"
              >
                {v === 0 ? "0" : formatOpsPlanHrs(v)}
              </text>
            </g>
          ))}
          {/* X-axis labels (categories) */}
          {categories.map((label, i) => (
            <text
              key={label}
              x={groupCenter(i)}
              y={chartBottom + 20}
              textAnchor="middle"
              fontSize={label.length > 12 ? 9 : 10}
              fill="#212529"
            >
              {label.length > 14 ? label.slice(0, 13) + "…" : label}
            </text>
          ))}
          {/* 3D Bars and value labels */}
          {data.map((d, i) => {
            const planH = (d.ytdPlan / yMax) * chartH;
            const actualH = (d.ytdActual / yMax) * chartH;
            const xPlan = barLeft(i, "plan");
            const xActual = barLeft(i, "actual");
            const d3 = AOP_CHART_3D_DEPTH;
            const drawBar3D = (x, y, w, h, baseColor) => {
              const topColor = lightenHex(baseColor, 0.35);
              const sideColor = darkenHex(baseColor, 0.25);
              // Right face (behind), then front, then top
              const rightPath = `M ${x + w} ${y} L ${x + w} ${y + h} L ${x + w + d3} ${y + h - d3} L ${x + w + d3} ${y - d3} Z`;
              const frontPath = `M ${x} ${y} L ${x + w} ${y} L ${x + w} ${y + h} L ${x} ${y + h} Z`;
              const topPath = `M ${x} ${y} L ${x + w} ${y} L ${x + w + d3} ${y - d3} L ${x + d3} ${y - d3} Z`;
              return (
                <g>
                  <path d={rightPath} fill={sideColor} stroke="none" />
                  <path d={frontPath} fill={baseColor} stroke="none" />
                  <path d={topPath} fill={topColor} stroke="none" />
                </g>
              );
            };
            return (
              <g key={d.label}>
                {drawBar3D(xPlan, yScale(d.ytdPlan), barWidth, planH, AOP_PROGRESS_CHART_COLORS.ytdPlan)}
                {drawBar3D(xActual, yScale(d.ytdActual), barWidth, actualH, AOP_PROGRESS_CHART_COLORS.ytdActual)}
                <text
                  x={xPlan + barWidth / 2}
                  y={yScale(d.ytdPlan) - 4}
                  textAnchor="middle"
                  fontSize={10}
                  fill="#212529"
                >
                  {formatOpsPlanHrs(d.ytdPlan)}
                </text>
                <text
                  x={xActual + barWidth / 2}
                  y={yScale(d.ytdActual) - 4}
                  textAnchor="middle"
                  fontSize={10}
                  fill="#212529"
                >
                  {formatOpsPlanHrs(d.ytdActual)}
                </text>
              </g>
            );
          })}
        </svg>
      </div>
    </div>
  );
}

function formatSopQuantity(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0";
  return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function parseSopActualEnteredValue(raw) {
  if (raw === undefined || raw === null || String(raw).trim() === "") {
    return { ok: true, value: 0 };
  }
  return parseOptionalFiniteNumber(raw, {
    min: 0,
    defaultIfEmpty: 0,
    fieldName: "actual_entered_hrs",
  });
}

const VALID_TABS = [TAB_AOP_BY_PROGRAM, TAB_SOP_BY_YEAR_PROGRAM, TAB_SOP];

function OperatingPlan() {
  const navigate = useNavigate();
  const userInfo = JSON.parse(sessionStorage.getItem("userInfo"));
  const account_id = userInfo.account_id;
  const serverUrl = uiConfig.serverInfo.serverUrl;
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get("tab");

  const [activeTab, setActiveTab] = useState(() =>
    tabFromUrl && VALID_TABS.includes(tabFromUrl) ? tabFromUrl : TAB_AOP_BY_PROGRAM
  );

  useEffect(() => {
    if (tabFromUrl === "aopProgress") {
      navigate("/OpsPlanDashboard", { replace: true });
      return;
    }
    if (tabFromUrl && VALID_TABS.includes(tabFromUrl)) {
      setActiveTab(tabFromUrl);
    }
  }, [tabFromUrl, navigate]);
  const [sopData, setSopData] = useState([]);
  const [programList, setProgramList] = useState([]); // programs from getProgram (for Program dropdown)
  const [bannerError, setBannerError] = useState("");

  // SOP form & import
  const [sopModal, setSopModal] = useState({ open: false, mode: "create", row: null });
  const [sopEventActuals, setSopEventActuals] = useState({});
  const [sopImportPanelExpanded, setSopImportPanelExpanded] = useState(false);
  const [sopImporting, setSopImporting] = useState(false);
  const [sopSelectedIds, setSopSelectedIds] = useState(new Set());
  const [sopDeleting, setSopDeleting] = useState(false);
  const [sopSearchTerm, setSopSearchTerm] = useState("");
  const [sopFiscalYearFilter, setSopFiscalYearFilter] = useState("");
  const [sopProgramFilter, setSopProgramFilter] = useState("");
  const [sopSortConfig, setSopSortConfig] = useState({ key: null, direction: "asc" });
  // SOP By Year & Program tab: "byProgram" | "allProgram"
  const [sopByYearViewMode, setSopByYearViewMode] = useState("byProgram");
  // SOP By Year & Program format: "graph" | "table"
  const [sopByYearFormat, setSopByYearFormat] = useState("graph");
  // SOP By Year & Program: program filter ("" = All Programs)
  const [sopByYearProgramFilter, setSopByYearProgramFilter] = useState("");
  // Current FY Start Date (page-level, for multiple calculations); stored YYYY-MM-DD
  const [currentFyStartDate, setCurrentFyStartDate] = useState(() => dayjs().startOf("year").format("YYYY-MM-DD"));
  // Current FY (page-level, for multiple calculations); stored in MongoDB
  const [currentFy, setCurrentFy] = useState(() => String(dayjs().year()));
  const [settingsEditMode, setSettingsEditMode] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: "",
    message: "",
    mode: "confirm", // "confirm" | "alert"
    onConfirm: null,
  });

  function openAlert(message, title = "Message") {
    setConfirmDialog({ open: true, title, message: message || "", mode: "alert", onConfirm: null });
  }

  useEffect(() => {
    fetchSOP();
    fetchPrograms();
    fetchOperatingPlanSettings();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only fetch
  }, []);

  function fetchOperatingPlanSettings() {
    apiFetch(serverUrl + "/getOperatingPlanSettings", {
      method: "POST",
      body: JSON.stringify({ account_id }),
    })
      .then((resp) => resp.json())
      .then((data) => {
        const date = ((data && data.current_fy_start_date) || "").trim();
        if (date && dayjs(date, "YYYY-MM-DD", true).isValid()) {
          setCurrentFyStartDate(date);
        }
        const fy = canonicalizeFiscalYear(((data && data.current_fy) || "").trim());
        if (fy) setCurrentFy(fy);
        // Default "All Fiscal Year" filter to Current FY from operating plan
        const fyForFilter = fy || canonicalizeFiscalYear(String(dayjs().year()));
        setSopFiscalYearFilter(fyForFilter);
      })
      .catch(() => {});
  }

  function saveOperatingPlanSettings(payload, onDone) {
    const body = { account_id };
    if (payload.current_fy_start_date !== undefined) {
      body.current_fy_start_date = (payload.current_fy_start_date || "").trim() || dayjs().startOf("year").format("YYYY-MM-DD");
    }
    if (payload.current_fy !== undefined) {
      body.current_fy = canonicalizeFiscalYear((payload.current_fy || "").trim())
        || canonicalizeFiscalYear(String(dayjs().year()));
    }
    apiFetch(serverUrl + "/saveOperatingPlanSettings", {
      method: "POST",
      body: JSON.stringify(body),
    })
      .then((resp) => {
        if (resp.ok && typeof onDone === "function") onDone();
      })
      .catch(() => setBannerError(getMessageForFetchError(null, "Failed to save Operating Plan settings.")));
  }

  function handleSaveSettings() {
    if (currentFyStartDate && currentFyStartDate.trim() !== "") {
      const dateCheck = validateOptionalDate(currentFyStartDate);
      if (!dateCheck.valid) {
        setBannerError(dateCheck.message || "Invalid FY start date.");
        return;
      }
    }
    saveOperatingPlanSettings(
      { current_fy: currentFy, current_fy_start_date: currentFyStartDate },
      () => {
        setSettingsEditMode(false);
        fetchOperatingPlanSettings();
      }
    );
  }

  function handleCancelSettingsEdit() {
    fetchOperatingPlanSettings();
    setSettingsEditMode(false);
  }

  function fetchPrograms() {
    apiFetch(serverUrl + "/getProgram", {
      method: "POST",
      body: JSON.stringify({ account_id }),
    })
      .then((resp) => resp.json())
      .then((data) => setProgramList(Array.isArray(data) ? data : []))
      .catch(() => setProgramList([]));
  }

  function fetchSopEventActuals(fiscalYears) {
    const years = [...new Set((fiscalYears || []).filter(Boolean))];
    if (!years.length) {
      setSopEventActuals({});
      return;
    }
    apiFetch(serverUrl + "/getSopEventActuals", {
      method: "POST",
      body: JSON.stringify({
        account_id,
        fiscal_years: years,
        asOfDate: dayjs().format("YYYY-MM-DD"),
      }),
    })
      .then((resp) => resp.json())
      .then((data) => setSopEventActuals(data?.actuals && typeof data.actuals === "object" ? data.actuals : {}))
      .catch(() => setSopEventActuals({}));
  }

  function getSopRowActuals(row) {
    const entered = Number(row?.actual_entered_hrs) || 0;
    const mapKey = sopEventActualMapKey(
      canonicalizeFiscalYear(row?.fiscal_year) || row?.fiscal_year,
      row?.program
    );
    const events = Number(sopEventActuals[mapKey]) || 0;
    return {
      entered,
      events,
      total: Math.round((entered + events) * 100) / 100,
    };
  }

  function buildSopEventsDrillPath(row) {
    return buildSopEventActualDrillPath({
      fiscalYear: row?.fiscal_year,
      programName: row?.program,
      programs: programList,
      currentFy,
      currentFyStartDate,
      asOfDate: dayjs().format("YYYY-MM-DD"),
    });
  }

  function findExistingSopRow(fiscalYear, program) {
    const prog = (program || "").trim();
    return sopData.find(
      (o) => fiscalYearsMatch(o.fiscal_year, fiscalYear) && (o.program || "").trim() === prog
    );
  }

  function fetchSOP() {
    apiFetch(serverUrl + "/getSOP", {
      method: "POST",
      body: JSON.stringify({ account_id }),
    })
      .then((resp) => resp.json())
      .then((data) => setSopData(Array.isArray(data) ? data : []))
      .catch((err) => setBannerError(getMessageForFetchError(err)));
  }

  useEffect(() => {
    if (activeTab !== TAB_SOP) return;
    fetchSopEventActuals(sopData.map((o) => o.fiscal_year));
  // eslint-disable-next-line react-hooks/exhaustive-deps -- refresh event actuals when SOP tab or data changes
  }, [activeTab, sopData]);

  function parseSOPCSV(csvText) {
    const { headers, rows } = parseCsvTextToNormalizedTable(csvText);
    const fyIdx = findCsvColumnIndex(headers, ["fiscal_year", "fiscalyear"]);
    const progIdx = findCsvColumnIndex(headers, ["program"]);
    const hoursIdx = findCsvColumnIndex(headers, ["planned_hrs"]);
    const enteredIdx = findCsvColumnIndex(headers, ["actual_entered_hrs"]);
    const revenueIdx = findCsvColumnIndex(headers, ["revenue"]);
    const expensesIdx = findCsvColumnIndex(headers, ["expenses"]);
    if (fyIdx === -1 || progIdx === -1 || hoursIdx === -1) {
      throw new Error("CSV must have columns: fiscal_year, program, planned_hrs");
    }
    const records = [];
    for (const values of rows) {
      if (values.length > Math.max(fyIdx, progIdx, hoursIdx)) {
        records.push({
          fiscal_year: values[fyIdx] || "",
          program: values[progIdx] || "",
          planned_hrs: (values[hoursIdx] ?? "").trim(),
          actual_entered_hrs: enteredIdx >= 0 ? (values[enteredIdx] ?? "").trim() : "",
          revenue: revenueIdx >= 0 ? (values[revenueIdx] ?? "").trim() : "",
          expenses: expensesIdx >= 0 ? (values[expensesIdx] ?? "").trim() : "",
        });
      }
    }
    return records;
  }

  async function importSOPFromCSV(file) {
    if (!file) return;
    const text = await file.text();
    await importSOPFromCsvText(text);
  }

  async function importSOPFromCsvText(text) {
    setSopImporting(true);
    try {
      const records = parseSOPCSV(text);
      if (records.length === 0) {
        openAlert("No valid records in CSV", "Import");
        setSopImporting(false);
        return;
      }
      let successCount = 0;
      let errorCount = 0;
      const errors = [];
      for (let i = 0; i < records.length; i++) {
        const r = records[i];
        if (!r.fiscal_year || !r.program) {
          errorCount++;
          errors.push(`Row ${i + 2}: Missing fiscal_year or program`);
          continue;
        }
        const plannedRes = parseOptionalFiniteNumber(r.planned_hrs, {
          min: 0,
          defaultIfEmpty: 0,
          fieldName: "planned_hrs",
        });
        if (!plannedRes.ok) {
          errorCount++;
          errors.push(`Row ${i + 2}: ${plannedRes.error}`);
          continue;
        }
        const enteredRes = parseSopActualEnteredValue(r.actual_entered_hrs);
        if (!enteredRes.ok) {
          errorCount++;
          errors.push(`Row ${i + 2}: ${enteredRes.error}`);
          continue;
        }
        const revenueRes = parseSopMoneyField(r.revenue, "revenue");
        if (!revenueRes.ok) {
          errorCount++;
          errors.push(`Row ${i + 2}: ${revenueRes.error}`);
          continue;
        }
        const expensesRes = parseSopMoneyField(r.expenses, "expenses");
        if (!expensesRes.ok) {
          errorCount++;
          errors.push(`Row ${i + 2}: ${expensesRes.error}`);
          continue;
        }
        const existing = findExistingSopRow(r.fiscal_year, r.program);
        try {
          const response = await apiFetch(serverUrl + (existing ? "/updateSOP" : "/createSOP"), {
            method: "POST",
            body: JSON.stringify({
              account_id,
              ...(existing ? { _id: existing._id } : {}),
              fiscal_year: r.fiscal_year,
              program: r.program,
              planned_hrs: plannedRes.value,
              actual_entered_hrs: enteredRes.value,
              revenue: revenueRes.value,
              expenses: expensesRes.value,
            }),
          });
          const data = await response.text();
          if (response.ok) successCount++;
          else {
            errorCount++;
            errors.push(`Row ${i + 2}: ${data}`);
          }
        } catch (err) {
          errorCount++;
          errors.push(`Row ${i + 2}: ${getMessageForFetchError(err)}`);
        }
      }
      let msg = `Import: Success ${successCount}, Errors ${errorCount}`;
      if (errors.length > 0 && errors.length <= 10) msg += "\n\n" + errors.join("\n");
      else if (errors.length > 10) msg += "\n\n" + errors.slice(0, 10).join("\n");
      openAlert(msg, "Import completed");
      fetchSOP();
    } catch (err) {
      setBannerError(getMessageForFetchError(err, "Error importing SOP CSV."));
    } finally {
      setSopImporting(false);
    }
  }

  function escapeCSVValue(val) {
    const s = String(val ?? "").trim();
    if (s.includes(",") || s.includes('"') || s.includes("\n") || s.includes("\r")) {
      return '"' + s.replace(/"/g, '""') + '"';
    }
    return s;
  }

  function getSelectedVisibleSopIds() {
    return intersectSelectedIdsWithRows(sopSelectedIds, getFilteredSortedSOP());
  }

  function exportSOPAsCSV() {
    const selectedVisibleIds = getSelectedVisibleSopIds();
    if (selectedVisibleIds.size === 0) {
      openAlert("Select at least one row to export.", "Export");
      return;
    }
    const header = "fiscal_year,program,planned_hrs,actual_entered_hrs,revenue,expenses";
    const idSet = selectedVisibleIds;
    const rows = getFilteredSortedSOP()
      .filter((row) => row && idSet.has(row._id))
      .map((row) =>
        [
          escapeCSVValue(row.fiscal_year),
          escapeCSVValue(row.program),
          escapeCSVValue(row.planned_hrs),
          escapeCSVValue(row.actual_entered_hrs ?? 0),
          escapeCSVValue(row.revenue ?? 0),
          escapeCSVValue(row.expenses ?? 0),
        ].join(",")
      );
    if (rows.length === 0) {
      openAlert("No matching records found for the current selection.", "Export");
      return;
    }
    const csv = [header, ...rows].join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "sop-export.csv";
    link.click();
    URL.revokeObjectURL(url);
  }

  function downloadSOPSampleCSV() {
    downloadCsvTextFile("sop-sample.csv", [
      "fiscal_year,program,planned_hrs,actual_entered_hrs,revenue,expenses",
      "FY2025,Program A,120,100,5000,4000",
      "FY2025,Program B,80,0,2500,2000",
    ]);
  }

  function handleSopFileChange(e) {
    attachValidatedCsvFileImport(e, importSOPFromCSV, (msg) => openAlert(msg, "Import"));
  }

  function openSopCreateModal() {
    setSopImportPanelExpanded(false);
    setSopModal({ open: true, mode: "create", row: null });
  }

  function openSopEditModal(row) {
    setSopImportPanelExpanded(false);
    setSopModal({ open: true, mode: "edit", row });
  }

  function closeSopModal() {
    setSopModal({ open: false, mode: "create", row: null });
  }

  async function saveSopFromModal(payload) {
    const isEdit = Boolean(payload._id);
    const response = await apiFetch(serverUrl + (isEdit ? "/updateSOP" : "/createSOP"), {
      method: "POST",
      body: JSON.stringify({
        account_id,
        ...(isEdit ? { _id: payload._id } : {}),
        fiscal_year: payload.fiscal_year,
        program: payload.program,
        planned_hrs: payload.planned_hrs,
        actual_entered_hrs: payload.actual_entered_hrs,
        revenue: payload.revenue,
        expenses: payload.expenses,
      }),
    });
    const text = await response.text();
    if (!response.ok) {
      throw new Error(text || "Failed to save Strategic Operating Plan.");
    }
    closeSopModal();
    fetchSOP();
    openAlert(text, "Strategic Operating Plan");
  }

  function deleteSOP(row) {
    apiFetch(serverUrl + "/deleteSOP", {
      method: "POST",
      body: JSON.stringify({ account_id, _id: row._id }),
    })
      .then((r) => r.text())
      .then((data) => {
        openAlert(data, "Strategic Operating Plan");
        setSopSelectedIds((prev) => {
          const next = new Set(prev);
          next.delete(row._id);
          return next;
        });
        fetchSOP();
      })
      .catch((err) => setBannerError(getMessageForFetchError(err)));
  }

  function getFilteredSortedSOP() {
    const term = (sopSearchTerm || "").toLowerCase();
    let list = sopData.filter((o) => {
      if (sopFiscalYearFilter && !fiscalYearsMatch(o.fiscal_year, sopFiscalYearFilter)) return false;
      if (sopProgramFilter && (o.program || "") !== sopProgramFilter) return false;
      if (term) {
        const matchFY = o.fiscal_year && o.fiscal_year.toLowerCase().includes(term);
        const matchProg = o.program && o.program.toLowerCase().includes(term);
        if (!matchFY && !matchProg) return false;
      }
      return true;
    });
    if (sopSortConfig.key) {
      list = [...list].sort((a, b) => {
        let av;
        let bv;
        if (sopSortConfig.key === "cost_per_kid_hour") {
          av = computeCostPerKidHour(a.expenses, getSopRowActuals(a).total);
          bv = computeCostPerKidHour(b.expenses, getSopRowActuals(b).total);
          if (av == null) av = -1;
          if (bv == null) bv = -1;
        } else {
          av = a[sopSortConfig.key] ?? "";
          bv = b[sopSortConfig.key] ?? "";
        }
        const cmp = av < bv ? -1 : av > bv ? 1 : 0;
        return sopSortConfig.direction === "asc" ? cmp : -cmp;
      });
    }
    return list;
  }

  function deleteSelectedSOP() {
    const selectedVisibleIds = getSelectedVisibleSopIds();
    if (selectedVisibleIds.size === 0) {
      openAlert("Select at least one row to delete.", "Delete SOP");
      return;
    }
    setConfirmDialog({
      open: true,
      title: "Delete SOP rows",
      message: `Delete ${selectedVisibleIds.size} record(s)?`,
      onConfirm: () => {
        setSopDeleting(true);
        const ids = Array.from(selectedVisibleIds);
        let done = 0;
        const run = (i) => {
          if (i >= ids.length) {
            setSopDeleting(false);
            setSopSelectedIds(new Set());
            fetchSOP();
            setConfirmDialog({
              open: true,
              title: "Delete completed",
              message: `Deleted ${done} record(s).`,
              onConfirm: null,
            });
            return;
          }
          apiFetch(serverUrl + "/deleteSOP", {
            method: "POST",
            body: JSON.stringify({ account_id, _id: ids[i] }),
          })
            .then((r) => {
              if (r.ok) done++;
              return run(i + 1);
            })
            .catch(() => run(i + 1));
        };
        run(0);
      },
    });
  }

  const sopFiltered = getFilteredSortedSOP();
  const sopSelectedVisibleIds = intersectSelectedIdsWithRows(sopSelectedIds, sopFiltered);
  const sopSelectedVisibleCount = sopSelectedVisibleIds.size;
  const sopSelectedCountSuffix = formatSelectedCountSuffix(sopSelectedVisibleCount);

  // Program dropdown options: group names from API; when editing, include current value if not in list
  const programOptionNames = React.useMemo(() => {
    const fromGroups = (programList || []).map((g) => (g.name || "").trim()).filter(Boolean);
    const set = new Set(fromGroups);
    const extra = [];
    const addProgram = (program) => {
      if (program && !set.has(program)) {
        extra.push(program);
        set.add(program);
      }
    };
    if (sopModal.open && sopModal.row?.program) addProgram(sopModal.row.program);
    return [...fromGroups, ...extra];
  }, [programList, sopModal]);

  // Filter dropdown options from current data
  const sopFiscalYears = React.useMemo(
    () => [...new Set(sopData.map((o) => o.fiscal_year).filter(Boolean))].sort(),
    [sopData]
  );

  useEffect(() => {
    if (!sopFiscalYearFilter || sopFiscalYears.length === 0) return;
    const resolved = resolveMatchingFiscalYear(sopFiscalYears, sopFiscalYearFilter);
    if (resolved !== sopFiscalYearFilter) {
      setSopFiscalYearFilter(resolved);
    }
  }, [sopFiscalYears, sopFiscalYearFilter]);

  const sopProgramsForFilter = React.useMemo(
    () => [...new Set(sopData.map((o) => o.program).filter(Boolean))].sort(),
    [sopData]
  );
  // Fiscal year filter options including Current FY so default selection is always valid
  const sopFiscalYearsForFilter = React.useMemo(
    () => {
      const set = new Set(sopFiscalYears);
      if (
        currentFy
        && ![...set].some((fy) => fiscalYearsMatch(fy, currentFy))
      ) {
        set.add(currentFy);
      }
      return [...set].sort();
    },
    [sopFiscalYears, currentFy]
  );
  // Fiscal year options for Current FY dropdown
  const fiscalYearOptions = React.useMemo(
    () => {
      const set = new Set(sopFiscalYears);
      if (
        currentFy
        && ![...set].some((fy) => fiscalYearsMatch(fy, currentFy))
      ) {
        set.add(currentFy);
      }
      return [...set].sort();
    },
    [sopFiscalYears, currentFy]
  );

  // Current SOP by program (pie): filter by Current FY, aggregate planned_hrs by program
  const aopByProgramData = React.useMemo(() => {
    const byProgram = {};
    sopData
      .filter((o) => fiscalYearsMatch(o.fiscal_year, currentFy))
      .forEach((o) => {
        const p = (o.program || "").trim() || "—";
        byProgram[p] = (byProgram[p] || 0) + (Number(o.planned_hrs) || 0);
      });
    const total = Object.values(byProgram).reduce((s, h) => s + h, 0);
    return Object.entries(byProgram)
      .map(([program, plannedHrs]) => ({
        program,
        planned_hrs: Math.round(plannedHrs * 100) / 100,
        pct: total > 0 ? Math.round((plannedHrs / total) * 1000) / 10 : 0,
      }))
      .sort((a, b) => b.planned_hrs - a.planned_hrs);
  }, [sopData, currentFy]);

  const aopByProgramTotalHrs = React.useMemo(
    () =>
      Math.round(
        aopByProgramData.reduce((s, row) => s + (Number(row.planned_hrs) || 0), 0) * 100
      ) / 100,
    [aopByProgramData]
  );

  const PIE_COLORS = ["#0d6efd", "#198754", "#fd7e14", "#6f42c1", "#20c997", "#ffc107", "#dc3545", "#0dcaf0", "#6c757d"];

  // Header-level: Today Date, Days Elapsed (FY Start → Today), % FY Elapsed (Days Elapsed / 365)
  const todayDate = dayjs().format("MM/DD/YYYY");
  const fyStartParsed = currentFyStartDate && dayjs(currentFyStartDate, "YYYY-MM-DD", true);
  const daysElapsed =
    fyStartParsed && fyStartParsed.isValid()
      ? Math.max(0, dayjs().diff(fyStartParsed, "day"))
      : null;
  const pctFyElapsed =
    daysElapsed != null ? Math.min(100, Math.round((daysElapsed / 365) * 1000) / 10) : null;

  // SOP By Year & Program: chart data sorted by FY ascending; byProgram = series per program, allProgram = one series
  const sopByYearChartData = React.useMemo(() => {
    const source = sopByYearProgramFilter
      ? sopData.filter((o) => (o.program || "").trim() === sopByYearProgramFilter)
      : sopData;
    if (!source.length) return { data: [], seriesKeys: [] };
    const years = [...new Set(source.map((o) => (o.fiscal_year || "").trim()).filter(Boolean))].sort(
      (a, b) => fySortKey(a) - fySortKey(b)
    );
    if (years.length === 0) return { data: [], seriesKeys: [] };
    if (sopByYearViewMode === "allProgram") {
      const data = years.map((year) => {
        const total = source
          .filter((o) => (o.fiscal_year || "").trim() === year)
          .reduce((s, o) => s + (Number(o.planned_hrs) || 0), 0);
        return { year, "All Program": Math.round(total * 100) / 100 };
      });
      return { data, seriesKeys: ["All Program"] };
    }
    const programs = [...new Set(source.map((o) => (o.program || "").trim() || "—").filter(Boolean))].sort();
    const data = years.map((year) => {
      const row = { year };
      const byProgram = {};
      source
        .filter((o) => (o.fiscal_year || "").trim() === year)
        .forEach((o) => {
          const p = (o.program || "").trim() || "—";
          byProgram[p] = (byProgram[p] || 0) + (Number(o.planned_hrs) || 0);
        });
      programs.forEach((p) => {
        row[p] = Math.round((byProgram[p] || 0) * 100) / 100;
      });
      return row;
    });
    return { data, seriesKeys: programs };
  }, [sopData, sopByYearViewMode, sopByYearProgramFilter]);

  const sopByYearTotals = React.useMemo(() => {
    const { data, seriesKeys } = sopByYearChartData;
    const byKey = {};
    seriesKeys.forEach((k) => {
      byKey[k] = 0;
    });
    let grand = 0;
    data.forEach((row) => {
      seriesKeys.forEach((k) => {
        const v = row[k] != null ? Number(row[k]) || 0 : 0;
        byKey[k] += v;
        grand += v;
      });
    });
    seriesKeys.forEach((k) => {
      byKey[k] = Math.round(byKey[k] * 100) / 100;
    });
    return { byKey, grand: Math.round(grand * 100) / 100 };
  }, [sopByYearChartData]);

  const sopTableTotals = React.useMemo(() => {
    let planned = 0;
    let entered = 0;
    let events = 0;
    let total = 0;
    let revenue = 0;
    let expenses = 0;
    sopFiltered.forEach((row) => {
      planned += Number(row.planned_hrs) || 0;
      const actuals = getSopRowActuals(row);
      entered += actuals.entered;
      events += actuals.events;
      total += actuals.total;
      revenue += Number(row.revenue) || 0;
      expenses += Number(row.expenses) || 0;
    });
    return {
      planned: Math.round(planned * 100) / 100,
      entered: Math.round(entered * 100) / 100,
      events: Math.round(events * 100) / 100,
      total: Math.round(total * 100) / 100,
      revenue: Math.round(revenue * 100) / 100,
      expenses: Math.round(expenses * 100) / 100,
    };
  }, [sopFiltered, sopEventActuals]);

  return (
    <div className="page-container page-container-full page-container--section-tabs page-container--ops-plan">
      <SectionTabsPageHeader>
        <OpsPlanSectionTabs activeTab={OPS_PLAN_TAB_MANAGEMENT} />
      </SectionTabsPageHeader>
      <div className="page-content">
        <div className="page-top">
          <h1 className="page-title">Operating Plan Management</h1>
          <p className="page-subtitle">AOP by program, SOP by year and program, and strategic operating plan.</p>
          <OperatingPlanFyStatsRow
            currentFy={currentFy}
            currentFyStartDate={currentFyStartDate}
            todayDate={todayDate}
            daysElapsed={daysElapsed}
            pctFyElapsed={pctFyElapsed}
            editMode={settingsEditMode}
            fiscalYearOptions={fiscalYearOptions}
            onCurrentFyChange={setCurrentFy}
            onCurrentFyStartDateChange={setCurrentFyStartDate}
            onEdit={() => setSettingsEditMode(true)}
            onSave={handleSaveSettings}
            onCancel={handleCancelSettingsEdit}
            showEdit
          />
        </div>
        <ErrorBanner message={bannerError} onDismiss={() => setBannerError("")} />
        <ConfirmationModal
          open={confirmDialog.open}
          title={confirmDialog.title || (confirmDialog.mode === "alert" ? "Message" : "Confirm")}
          message={confirmDialog.message}
          mode={confirmDialog.mode === "alert" ? "alert" : "confirm"}
          confirmLabel={confirmDialog.mode === "alert" ? "OK" : undefined}
          onConfirm={() => {
            const fn = confirmDialog.onConfirm;
            setConfirmDialog({ open: false, title: "", message: "", mode: "confirm", onConfirm: null });
            if (fn) fn();
          }}
          onCancel={() =>
            setConfirmDialog({ open: false, title: "", message: "", mode: "confirm", onConfirm: null })
          }
        />
        <SopEditorModal
          open={sopModal.open}
          mode={sopModal.mode}
          row={sopModal.row}
          programOptions={programOptionNames}
          defaultFiscalYear={currentFy}
          onCancel={closeSopModal}
          onSubmit={saveSopFromModal}
        />
        <div className="tabs-bar">
          <div className="tabs-list">
            <button
              type="button"
              className={`tab-btn ${activeTab === TAB_AOP_BY_PROGRAM ? "active" : ""}`}
              onClick={() => { setActiveTab(TAB_AOP_BY_PROGRAM); setSearchParams({ tab: TAB_AOP_BY_PROGRAM }); }}
            >
              Current AOP By Program
            </button>
            <button
              type="button"
              className={`tab-btn ${activeTab === TAB_SOP_BY_YEAR_PROGRAM ? "active" : ""}`}
              onClick={() => { setActiveTab(TAB_SOP_BY_YEAR_PROGRAM); setSearchParams({ tab: TAB_SOP_BY_YEAR_PROGRAM }); }}
            >
              SOP By Year & Program
            </button>
            <button
              type="button"
              className={`tab-btn ${activeTab === TAB_SOP ? "active" : ""}`}
              onClick={() => { setActiveTab(TAB_SOP); setSearchParams({ tab: TAB_SOP }); }}
            >
              Strategic Operating Plan
            </button>
          </div>
        </div>

        {activeTab === TAB_SOP && (
          <>
            <ImportCsvPanel
              expanded={sopImportPanelExpanded}
              fileInputId="sop-csv-import"
              importing={sopImporting}
              columnHint={OPS_PLAN_CSV_COLUMN_HINT}
              onDownloadSample={downloadSOPSampleCSV}
              onFileChange={handleSopFileChange}
              onImportText={importSOPFromCsvText}
            />
            <div className="toolbar">
              <select value={sopFiscalYearFilter} onChange={(e) => setSopFiscalYearFilter(e.target.value)} className="input input-min-100" title="Filter by Fiscal Year">
                <option value="">All Fiscal Years</option>
                {sopFiscalYearsForFilter.map((fy) => (
                  <option key={fy} value={fy}>{fy}</option>
                ))}
              </select>
              <select value={sopProgramFilter} onChange={(e) => setSopProgramFilter(e.target.value)} className="input input-min-120" title="Filter by Program">
                <option value="">All Programs</option>
                {sopProgramsForFilter.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
              <input type="text" value={sopSearchTerm} onChange={(e) => setSopSearchTerm(e.target.value)} placeholder="Search fiscal year, program…" className="input input-min-140 input-max-400" />
              <span className="toolbar-divider" />
              <button
                type="button"
                onClick={exportSOPAsCSV}
                disabled={sopSelectedVisibleCount === 0}
                className="btn btn-md btn-primary-outline"
              >
                Export CSV{sopSelectedCountSuffix}
              </button>
              <button
                type="button"
                onClick={() => {
                  setSopImportPanelExpanded(!sopImportPanelExpanded);
                }}
                className={sopImportPanelExpanded ? "btn btn-primary-outline" : "btn btn-primary"}
              >
                {sopImportPanelExpanded ? "− Import CSV" : "+ Import CSV"}
              </button>
              <button type="button" onClick={openSopCreateModal} className="btn btn-primary">
                + New
              </button>
              <button type="button" onClick={deleteSelectedSOP} disabled={sopSelectedVisibleCount === 0 || sopDeleting} className="btn btn-danger">
                Delete{sopSelectedCountSuffix}
              </button>
            </div>
          </>
        )}

        {activeTab === TAB_AOP_BY_PROGRAM && (
          <div className="panel-hint page-section">{formatSopPlanHrsByProgramHint(currentFy)}</div>
        )}
        <div className="table-scroll">
        {activeTab === TAB_SOP && (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      checked={sopFiltered.length > 0 && sopFiltered.every((o) => sopSelectedIds.has(o._id))}
                      onChange={(e) => {
                        const next = new Set(sopSelectedIds);
                        if (e.target.checked) sopFiltered.forEach((o) => next.add(o._id));
                        else sopFiltered.forEach((o) => next.delete(o._id));
                        setSopSelectedIds(next);
                      }}
                      disabled={sopFiltered.length === 0}
                    />
                  </th>
                  <th
                    className="sortable table-th-nowrap"
                    onClick={() => setSopSortConfig({ key: "fiscal_year", direction: sopSortConfig.key === "fiscal_year" && sopSortConfig.direction === "asc" ? "desc" : "asc" })}
                  >
                    Fiscal Year
                  </th>
                  <th
                    className="sortable table-th-nowrap"
                    onClick={() => setSopSortConfig({ key: "program", direction: sopSortConfig.key === "program" && sopSortConfig.direction === "asc" ? "desc" : "asc" })}
                  >
                    Program
                  </th>
                  <th
                    className="sortable table-cell-numeric"
                    onClick={() => setSopSortConfig({ key: "planned_hrs", direction: sopSortConfig.key === "planned_hrs" && sopSortConfig.direction === "asc" ? "desc" : "asc" })}
                  >
                    {OPS_PLAN_LABEL_PLAN_HRS}
                  </th>
                  <th
                    className="sortable table-cell-numeric"
                    title={OPS_PLAN_LABEL_ACTUAL_ENTERED_HRS}
                    onClick={() => setSopSortConfig({ key: "actual_entered_hrs", direction: sopSortConfig.key === "actual_entered_hrs" && sopSortConfig.direction === "asc" ? "desc" : "asc" })}
                  >
                    {OPS_PLAN_SOP_COL_ENTERED_HRS}
                  </th>
                  <th
                    className="table-cell-numeric"
                    title={OPS_PLAN_ACTUAL_EVENTS_TITLE}
                  >
                    {OPS_PLAN_SOP_COL_EVENTS_HRS}
                  </th>
                  <th
                    className="table-cell-numeric"
                    title={OPS_PLAN_LABEL_ACTUAL_TOTAL_HRS}
                  >
                    {OPS_PLAN_SOP_COL_TOTAL_HRS}
                  </th>
                  <th
                    className="sortable table-cell-numeric"
                    onClick={() => setSopSortConfig({ key: "revenue", direction: sopSortConfig.key === "revenue" && sopSortConfig.direction === "asc" ? "desc" : "asc" })}
                  >
                    {OPS_PLAN_SOP_COL_REVENUE}
                  </th>
                  <th
                    className="sortable table-cell-numeric"
                    onClick={() => setSopSortConfig({ key: "expenses", direction: sopSortConfig.key === "expenses" && sopSortConfig.direction === "asc" ? "desc" : "asc" })}
                  >
                    {OPS_PLAN_SOP_COL_EXPENSES}
                  </th>
                  <th
                    className="sortable table-cell-numeric"
                    title={OPS_PLAN_SOP_COST_PER_KID_HOUR_TITLE}
                    onClick={() => setSopSortConfig({ key: "cost_per_kid_hour", direction: sopSortConfig.key === "cost_per_kid_hour" && sopSortConfig.direction === "asc" ? "desc" : "asc" })}
                  >
                    {OPS_PLAN_SOP_COL_COST_PER_KID_HOUR}
                  </th>
                  <TableActionColumnHeader className="table-th-nowrap" align="left" />
                </tr>
              </thead>
              <tbody>
                {sopFiltered.map((row) => {
                  const actuals = getSopRowActuals(row);
                  const eventsDrillPath =
                    actuals.events > 0 ? buildSopEventsDrillPath(row) : null;
                  return (
                    <tr key={row._id}>
                      <td>
                        <input
                          type="checkbox"
                          checked={sopSelectedIds.has(row._id)}
                          onChange={(e) => {
                            const next = new Set(sopSelectedIds);
                            if (e.target.checked) next.add(row._id);
                            else next.delete(row._id);
                            setSopSelectedIds(next);
                          }}
                        />
                      </td>
                      <td>{row.fiscal_year}</td>
                      <td>{row.program}</td>
                      <td className="table-cell-numeric">{formatSopQuantity(row.planned_hrs)}</td>
                      <td className="table-cell-numeric">{formatSopQuantity(actuals.entered)}</td>
                      <td className="table-cell-numeric">
                        {eventsDrillPath ? (
                          <Link
                            to={eventsDrillPath}
                            style={sopActualDrillLinkStyle}
                            title="View closed events contributing to event actual"
                          >
                            {formatSopQuantity(actuals.events)}
                          </Link>
                        ) : (
                          formatSopQuantity(actuals.events)
                        )}
                      </td>
                      <td className="table-cell-numeric" style={{ fontWeight: 500 }}>{formatSopQuantity(actuals.total)}</td>
                      <td className="table-cell-numeric">{formatCurrencyUsd(row.revenue ?? 0)}</td>
                      <td className="table-cell-numeric">{formatCurrencyUsd(row.expenses ?? 0)}</td>
                      <td className="table-cell-numeric">{formatCostPerKidHour(row.expenses, actuals.total)}</td>
                      <td>
                        <RowActionIcons>
                          <button type="button" className="btn-link" style={iconBtnStyle} title="Edit" onClick={() => openSopEditModal(row)}><IconEdit size={18} /></button>
                          <button type="button" className="btn-link-danger" style={iconBtnStyle} title="Delete" onClick={() => deleteSOP(row)}><IconDelete size={18} /></button>
                        </RowActionIcons>
                      </td>
                    </tr>
                  );
                })}
                {sopFiltered.length > 0 && (
                  <tr className="table-row-total">
                    <td />
                    <td>Total</td>
                    <td />
                    <td className="table-cell-numeric">{formatSopQuantity(sopTableTotals.planned)}</td>
                    <td className="table-cell-numeric">{formatSopQuantity(sopTableTotals.entered)}</td>
                    <td className="table-cell-numeric">{formatSopQuantity(sopTableTotals.events)}</td>
                    <td className="table-cell-numeric">{formatSopQuantity(sopTableTotals.total)}</td>
                    <td className="table-cell-numeric">{formatCurrencyUsd(sopTableTotals.revenue)}</td>
                    <td className="table-cell-numeric">{formatCurrencyUsd(sopTableTotals.expenses)}</td>
                    <td className="table-cell-numeric">{formatCostPerKidHour(sopTableTotals.expenses, sopTableTotals.total)}</td>
                    <td />
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === TAB_AOP_BY_PROGRAM && (
          <div style={{ padding: "24px", display: "flex", flexWrap: "wrap", alignItems: "flex-start", gap: "32px" }}>
            <div style={{ flexShrink: 0 }}>
              {aopByProgramData.length > 0 ? (
                <div
                  style={{
                    width: "280px",
                    height: "280px",
                    borderRadius: "50%",
                    background: `conic-gradient(${aopByProgramData
                      .map((s, i) => {
                        const start = aopByProgramData
                          .slice(0, i)
                          .reduce((sum, x) => sum + x.pct, 0);
                        const end = start + s.pct;
                        return `${PIE_COLORS[i % PIE_COLORS.length]} ${start}% ${end}%`;
                      })
                      .join(", ")})`,
                  }}
                  title={aopByProgramData.map((s) => `${s.program}: ${s.pct}%`).join("\n")}
                />
              ) : (
                <div
                  style={{
                    width: "280px",
                    height: "280px",
                    borderRadius: "50%",
                    background: "#f0f0f0",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "14px",
                    color: "#6c757d",
                  }}
                >
                  No SOP data for {currentFy || "this FY"}
                </div>
              )}
            </div>
            <div style={{ flex: "1", minWidth: "200px" }}>
              <div className="panel-title" style={{ marginBottom: 8 }}>By program %</div>
              <div className="table-wrapper" style={{ display: "inline-block", maxWidth: "100%" }}>
                <table style={{ width: "100%", maxWidth: "400px" }}>
                  <thead>
                    <tr>
                      <th>Program</th>
                      <th className="table-cell-numeric">{OPS_PLAN_LABEL_PLAN_HRS}</th>
                      <th className="table-cell-numeric">%</th>
                    </tr>
                  </thead>
                  <tbody>
                    {aopByProgramData.map((s, i) => (
                      <tr key={s.program}>
                        <td>
                          <span className="badge" style={{ width: 12, height: 12, padding: 0, marginRight: 8, borderRadius: 2, backgroundColor: PIE_COLORS[i % PIE_COLORS.length], verticalAlign: "middle" }} aria-hidden />
                          {s.program}
                        </td>
                        <td className="table-cell-numeric">{s.planned_hrs.toLocaleString()}</td>
                        <td className="table-cell-numeric" style={{ fontWeight: 500 }}>{s.pct}%</td>
                      </tr>
                    ))}
                    {aopByProgramData.length > 0 && (
                      <tr className="table-row-total">
                        <td>Total</td>
                        <td className="table-cell-numeric">{aopByProgramTotalHrs.toLocaleString()}</td>
                        <td className="table-cell-numeric">100%</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {aopByProgramData.length === 0 && (
                <p className="empty-message">No SOP records for Current FY. Use the SOP tab to add data, or change Current FY above.</p>
              )}
            </div>
          </div>
        )}

        {activeTab === TAB_SOP_BY_YEAR_PROGRAM && (
          <div style={{ padding: "16px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "16px", marginBottom: "12px", flexWrap: "wrap" }}>
              <select
                value={sopByYearProgramFilter}
                onChange={(e) => setSopByYearProgramFilter(e.target.value)}
                className="input input-min-120"
                title="Filter by Program"
              >
                <option value="">All Programs</option>
                {sopProgramsForFilter.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
              <span style={{ marginLeft: "8px", borderLeft: "1px solid #dee2e6", paddingLeft: "16px", fontSize: "13px", fontWeight: 600 }}>View:</span>
              <label style={{ display: "flex", alignItems: "center", gap: "6px", cursor: "pointer", fontSize: "13px" }}>
                <input
                  type="radio"
                  name="sopByYearView"
                  checked={sopByYearViewMode === "byProgram"}
                  onChange={() => setSopByYearViewMode("byProgram")}
                />
                By Program
              </label>
              <label style={{ display: "flex", alignItems: "center", gap: "6px", cursor: "pointer", fontSize: "13px" }}>
                <input
                  type="radio"
                  name="sopByYearView"
                  checked={sopByYearViewMode === "allProgram"}
                  onChange={() => setSopByYearViewMode("allProgram")}
                />
                All Program
              </label>
              {sopByYearViewMode === "byProgram" && (
                <>
                  <span style={{ marginLeft: "8px", borderLeft: "1px solid #dee2e6", paddingLeft: "16px", fontSize: "13px", fontWeight: 600 }}>Format:</span>
                  <label style={{ display: "flex", alignItems: "center", gap: "6px", cursor: "pointer", fontSize: "13px" }}>
                    <input
                      type="radio"
                      name="sopByYearFormat"
                      checked={sopByYearFormat === "graph"}
                      onChange={() => setSopByYearFormat("graph")}
                    />
                    Graph
                  </label>
                  <label style={{ display: "flex", alignItems: "center", gap: "6px", cursor: "pointer", fontSize: "13px" }}>
                    <input
                      type="radio"
                      name="sopByYearFormat"
                      checked={sopByYearFormat === "table"}
                      onChange={() => setSopByYearFormat("table")}
                    />
                    Table
                  </label>
                </>
              )}
            </div>
            {sopByYearChartData.data.length > 0 ? (
              (sopByYearFormat === "graph" || sopByYearViewMode === "allProgram") ? (
                <>
                  <SopByYearBarChart
                    data={sopByYearChartData.data}
                    seriesKeys={sopByYearChartData.seriesKeys}
                    colors={PIE_COLORS}
                  />
                  <div style={{ marginTop: 12, fontWeight: 600, fontSize: 14 }}>
                    Total {OPS_PLAN_LABEL_PLAN_HRS}: {sopByYearTotals.grand.toLocaleString()}
                  </div>
                </>
              ) : (
                <div className="table-wrapper" style={{ display: "inline-block", overflowX: "auto" }}>
                  <table style={{ width: "auto", minWidth: "min-content" }}>
                    <thead>
                      <tr>
                        <th>Fiscal Year</th>
                        {sopByYearViewMode !== "allProgram" && sopByYearChartData.seriesKeys.map((key) => (
                          <th key={key} className="table-cell-numeric">{key}</th>
                        ))}
                        <th className="table-cell-numeric">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sopByYearChartData.data.map((row) => {
                        const rowTotal = sopByYearChartData.seriesKeys.reduce(
                          (s, k) => s + (row[k] != null ? row[k] : 0),
                          0
                        );
                        return (
                          <tr key={row.year}>
                            <td>{row.year}</td>
                            {sopByYearViewMode !== "allProgram" && sopByYearChartData.seriesKeys.map((key) => (
                              <td key={key} className="table-cell-numeric">
                                {(row[key] != null ? row[key] : 0).toLocaleString()}
                              </td>
                            ))}
                            <td className="table-cell-numeric" style={{ fontWeight: 500 }}>
                              {(Math.round(rowTotal * 100) / 100).toLocaleString()}
                            </td>
                          </tr>
                        );
                      })}
                      <tr className="table-row-total">
                        <td>Total</td>
                        {sopByYearViewMode !== "allProgram" && sopByYearChartData.seriesKeys.map((key) => (
                          <td key={key} className="table-cell-numeric">
                            {(sopByYearTotals.byKey[key] || 0).toLocaleString()}
                          </td>
                        ))}
                        <td className="table-cell-numeric">
                          {sopByYearTotals.grand.toLocaleString()}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )
            ) : (
              <p className="empty-message">{OPS_PLAN_SOP_EMPTY_MESSAGE}</p>
            )}
          </div>
        )}
        </div>
      </div>
    </div>
  );
}

export default OperatingPlan;
